/**
 * Every word and threshold on the result screen, in one place.
 *
 * The respondent sees this on finishing (components/Results.tsx). Mastery sees
 * the same thing on the lead's page in the dashboard (admin/RespondentView.tsx).
 * Those two must agree exactly — someone picking up the phone is talking to a
 * person who has already read this, and being told something different from
 * what they were shown is worse than being told nothing.
 *
 * That congruence is why the copy lives here rather than in either component.
 * Two hard-coded copies of a sentence stay identical right up until one of them
 * is improved, and nothing fails when they diverge — the dashboard just quietly
 * starts lying about what the client was told.
 *
 * Nothing here is scoring logic. Bands and category scores arrive from the API
 * already decided; this only chooses how they read.
 */

export interface ScoredCategory {
  category: string;
  score: number;
}

/** Presentation for each band. `say` is the paragraph under the score. */
export const BAND_STYLE: Record<string, { bg: string; ink: string; say: string }> = {
  "Not Ready": {
    bg: "var(--critical)",
    ink: "#fff",
    say: "There are material gaps here — the kind that reduce offers or stall a deal once a buyer starts looking closely. That's fixable, and finding it now rather than mid-diligence is the point.",
  },
  "Needs Work": {
    bg: "var(--warning)",
    ink: "#0E1020",
    say: "The foundation is there. A handful of specific gaps stand between this business and a clean transition — and they're the sort that respond well to focused work over 12–24 months.",
  },
  Ready: {
    bg: "var(--good)",
    ink: "#fff",
    say: "This business is well positioned. The work from here is fine-tuning and timing rather than repair — worth protecting what's already been built.",
  },
};

/**
 * What each band means, so a result is read in context rather than as a bare
 * label. Ordered best to worst — a respondent looking for their own level
 * reads down, and seeing "Ready" first frames the rest as distance from it.
 *
 * Deliberately carries no numeric cutoffs. Those are the one part of the
 * scoring model that isn't client-facing, and they move without a deploy, so
 * printing them here would both leak the model and go stale.
 *
 * Mirrored in api/app/email_sender.py — see the note on BAND_STYLE.
 */
export const BAND_DEFINITIONS: { band: string; means: string }[] = [
  {
    band: "Ready",
    means:
      "Well positioned for a transition on your terms. The work from here is timing and protecting what's already built.",
  },
  {
    band: "Needs Work",
    means:
      "Sound in most respects, with specific gaps that hold back value or slow a deal. Usually 12–24 months of focused work.",
  },
  {
    band: "Not Ready",
    means:
      "Material gaps a buyer would price against, or walk from. The work is finding and closing them before anyone looks closely.",
  },
];

export const FALLBACK_BAND_STYLE = { bg: "var(--warning)", ink: "#0E1020", say: "" };

export function bandStyle(band: string) {
  return BAND_STYLE[band] ?? FALLBACK_BAND_STYLE;
}

/**
 * Bar colour by score.
 *
 * Thresholds are deliberately finer than the three bands. A category at 58 and
 * one at 71 are both "Needs Work" and are not the same conversation, and the
 * whole point of the by-area breakdown is to show that difference.
 */
export function heat(score: number): string {
  if (score < 45) return "var(--heat-1)";
  if (score < 60) return "var(--heat-2)";
  if (score < 72) return "var(--heat-3)";
  if (score < 80) return "var(--heat-4)";
  return "var(--heat-5)";
}

/**
 * The strongest and weakest areas, or null when naming them would be silly.
 *
 * Null when there is one category, or when every category scored the same —
 * "your strongest area is X and your weakest is X" reads as a bug, and on a
 * flat set the ordering is an artefact of the sort rather than a finding.
 */
export function focus(
  categories: ScoredCategory[],
): { strongest: ScoredCategory; weakest: ScoredCategory } | null {
  if (categories.length < 2) return null;

  const sorted = [...categories].sort((a, b) => a.score - b.score);
  const weakest = sorted[0];
  const strongest = sorted[sorted.length - 1];

  if (!weakest || !strongest) return null;
  if (weakest.category === strongest.category) return null;
  if (weakest.score === strongest.score) return null;

  return { strongest, weakest };
}

/**
 * The sentence naming those two areas, in three pieces around the names so
 * each screen can set them in bold.
 *
 * It used to end "that's where the full report starts", about a report that
 * doesn't exist: the email repeats this summary, it doesn't go further.
 *
 * Mirrored in api/app/email_sender.py.
 */
export const FOCUS_LINE = [
  "Your strongest area is ",
  ". The one holding you back most is ",
  " — that's the place to start.",
] as const;

/**
 * The offer under the result, on screen and in the email.
 *
 * Mirrored in api/app/email_sender.py.
 */
export const CONTACT_LABEL = "Talk it through with Mastery Partners";
export const CONTACT_URL = "https://www.masterypartners.com/contact";

/**
 * What the total percentage is called, wherever anyone reads it.
 *
 * Tom's name for it. It had been "Your readiness" on this screen, "Overall" on
 * the lead page and in the staff alert, and "Overall score" in Zoho — one number
 * under three names, which is how the respondent and the person ringing them
 * end up sounding like they are discussing different things.
 *
 * Mirrored in api/app/email_sender.py and in the Zoho notes
 * (api/app/integrations/zoho/mapping.py).
 */
export const SCORE_LABEL = "Readiness Score";

/**
 * The closing caveat, shown to the respondent and repeated to Mastery.
 *
 * It matters that this is verbatim in both places: it is the sentence that
 * keeps a mini-assessment from being mistaken for an Opinion of Value, and the
 * person on the call needs to know it is the expectation already set.
 *
 * It deliberately promises nothing about what comes next. It used to describe a
 * 500-point full assessment, and that engagement isn't settled — the caveat is
 * the last place to be describing something that may never be offered.
 *
 * Mirrored in api/app/email_sender.py.
 */
export const NOT_A_VALUATION = "This is an indication of readiness, not a valuation.";

/**
 * What the respondent was told about the report reaching them.
 *
 * The email is this screen plus what each level means, so that is all the
 * promise names. It used to offer "what to do first" as well, which the email
 * never contained. Results.tsx sets the same words with emphasis.
 */
export function reportPromise(emailQueued: boolean, email: string): string {
  return emailQueued
    ? `Check your inbox. A copy of these results is on its way to ${email}, with what each level means.`
    : `Your results are saved. We'll be in touch at ${email} with the full breakdown shortly.`;
}
